import React, { useState } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Dimensions,
  Alert
} from 'react-native';
import {
  Text,
  Card,
  Title,
  Paragraph,
  TextInput,
  Button,
  Surface,
  Chip
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { 
  colors, 
  spacing, 
  borderRadius, 
  shadows, 
  typography,
  commonStyles,
  getCardMaxWidth,
  getPadding 
} from './sharedStyles';

export default function UniversalYearComponent() {
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [result, setResult] = useState(null);

  const { width } = Dimensions.get('window');
  const isDesktop = width >= 1024;
  const isMobile = width < 768;

  const calculateUniversalYear = (inputYear) => {
    const yearNum = parseInt(inputYear);
    if (isNaN(yearNum) || yearNum < 1000 || yearNum > 9999) {
      return null;
    }

    let sum = yearNum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    
    while (sum > 9 && sum !== 11 && sum !== 22) {
      sum = sum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0);
    }
    
    return {
      year: yearNum,
      universalYear: sum,
      calculation: yearNum.toString().split('').join(' + ') + ' = ' + 
                   yearNum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0) +
                   (sum !== yearNum.toString().split('').reduce((acc, digit) => acc + parseInt(digit), 0) ? 
                    ' → ' + sum : '')
    };
  };

  const getUniversalYearMeaning = (number) => {
    const meanings = {
      1: {
        title: "Year of New Beginnings",
        description: "Global innovation, independence, and leadership. A time for starting fresh initiatives and pioneering new paths.",
        themes: ["Innovation", "Leadership", "Independence", "New starts"]
      },
      2: {
        title: "Year of Cooperation",
        description: "Focus on diplomacy, partnerships, and working together. A time for patience, sensitivity, and collaborative efforts.",
        themes: ["Partnerships", "Diplomacy", "Patience", "Cooperation"]
      },
      3: {
        title: "Year of Creative Expression",
        description: "Communication, artistic endeavors, and social expansion. A time for creativity, optimism, and self-expression.",
        themes: ["Creativity", "Communication", "Arts", "Social growth"]
      },
      4: {
        title: "Year of Building Foundations",
        description: "Hard work, discipline, and establishing solid structures. A time for practical efforts and building for the future.",
        themes: ["Hard work", "Discipline", "Stability", "Foundations"]
      },
      5: {
        title: "Year of Change and Freedom",
        description: "Adventure, variety, and progressive change. A time for breaking free from limitations and embracing new experiences.",
        themes: ["Change", "Freedom", "Adventure", "Progress"]
      },
      6: {
        title: "Year of Responsibility",
        description: "Focus on community, family, and service to others. A time for nurturing, healing, and taking care of responsibilities.",
        themes: ["Community", "Service", "Family", "Healing"]
      },
      7: {
        title: "Year of Spiritual Awakening",
        description: "Introspection, spiritual growth, and seeking deeper truths. A time for inner development and understanding.",
        themes: ["Spirituality", "Introspection", "Truth", "Inner growth"]
      },
      8: {
        title: "Year of Material Mastery",
        description: "Business success, material achievement, and power dynamics. A time for ambitious goals and material progress.",
        themes: ["Success", "Achievement", "Power", "Material gains"]
      },
      9: {
        title: "Year of Completion",
        description: "Endings, humanitarian efforts, and universal wisdom. A time for letting go and serving the greater good.",
        themes: ["Completion", "Humanitarian", "Wisdom", "Service"]
      },
      11: {
        title: "Year of Illumination",
        description: "Spiritual insight, intuition, and enlightenment. A master number year bringing higher consciousness and inspiration.",
        themes: ["Illumination", "Intuition", "Inspiration", "Higher consciousness"]
      },
      22: {
        title: "Year of Master Building",
        description: "Large-scale achievements and visionary projects. A master number year for building something significant and lasting.",
        themes: ["Master building", "Vision", "Large achievements", "Legacy"]
      }
    };
    
    return meanings[number] || { title: "Unknown", description: "", themes: [] };
  };

  const handleCalculate = () => {
    if (!year.trim()) {
      Alert.alert('Error', 'Please enter a year');
      return;
    }

    const calculation = calculateUniversalYear(year);
    if (!calculation) {
      Alert.alert('Error', 'Please enter a valid 4-digit year');
      return;
    }

    setResult(calculation);
  };

  const resetCalculation = () => {
    setYear(new Date().getFullYear().toString());
    setResult(null);
  };

  return (
    <View style={commonStyles.container}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.mainContent}>
          {/* Input Section */}
          <Surface style={[commonStyles.solidCard, styles.inputCard]}>
            {/* Header Section */}
            <View style={styles.headerContainer}>
              <LinearGradient
                colors={['rgba(74, 57, 117, 0.1)', 'rgba(102, 126, 234, 0.1)']}
                style={styles.headerIconContainer}
              >
                <Ionicons name="calendar" size={32} color={colors.primary} />
              </LinearGradient>
              <Title style={styles.headerTitle}>Universal Year Calculator</Title>
              <Text style={styles.headerSubtitle}>
                Discover the collective energy and themes for any year
              </Text>
            </View>

            <View style={styles.inputFieldContainer}>
              <View style={styles.fieldWrapper}>
                <Text style={styles.fieldLabel}>Year</Text>
                <Surface style={styles.inputWrapper}>
                  <View style={styles.inputWithIcon}>
                    <Ionicons 
                      name="calendar-outline" 
                      size={20} 
                      color={colors.primary} 
                      style={styles.inputIcon} 
                    />
                    <TextInput
                      value={year}
                      onChangeText={setYear}
                      onSubmitEditing={handleCalculate}
                      style={styles.modernInput}
                      mode="outlined"
                      placeholder="YYYY"
                      placeholderTextColor={colors.textSecondary + '80'}
                      keyboardType="numeric"
                      maxLength={4}
                      returnKeyType="done"
                      theme={{
                        colors: {
                          primary: colors.primary,
                          outline: colors.primary + '30',
                        },
                      }}
                      outlineColor="transparent"
                      activeOutlineColor={colors.primary}
                    />
                  </View>
                </Surface>
              </View>

              <View style={styles.buttonContainer}>
                <LinearGradient
                  colors={[colors.primary, colors.primaryLight]}
                  style={styles.gradientButton}
                  start={{ x: 0, y: 0 }}
                  end={{ x: 1, y: 0 }}
                >
                  <Button
                    mode="contained"
                    onPress={handleCalculate}
                    style={styles.calculateButton}
                    labelStyle={styles.calculateButtonText}
                    contentStyle={styles.calculateButtonContent}
                    buttonColor="transparent"
                  >
                    <Ionicons name="analytics" size={18} color={colors.white} style={styles.buttonIcon} />
                    Calculate Universal Year
                  </Button>
                </LinearGradient>
                
                {result && (
                  <Button
                    mode="outlined"
                    onPress={resetCalculation}
                    style={styles.resetButton}
                    labelStyle={styles.resetButtonText}
                  >
                    <Ionicons name="refresh" size={16} color={colors.primary} />
                    Reset
                  </Button>
                )}
              </View>
            </View>
          </Surface>

          {result && (
            <View style={styles.resultsContainer}>
              <Surface style={[styles.resultCard, isDesktop && styles.desktopResultCard]}>
                <View style={styles.resultHeader}>
                  <Title style={styles.yearTitle}>{result.year}</Title>
                  <LinearGradient
                    colors={[colors.primary, colors.primaryLight]}
                    style={styles.universalYearBadge}
                    start={{ x: 0, y: 0 }}
                    end={{ x: 1, y: 1 }}
                  >
                    <Text style={styles.universalYearNumber}>{result.universalYear}</Text>
                    <Text style={styles.universalYearLabel}>Universal Year</Text>
                  </LinearGradient>
                </View>
                
                <Card style={styles.calculationCard}>
                  <Card.Content>
                    <Text style={styles.calculationLabel}>Calculation:</Text>
                    <Text style={styles.calculationText}>{result.calculation}</Text>
                  </Card.Content>
                </Card>
              </Surface>

              {result && (
                <Card style={[styles.meaningCard, isDesktop && styles.desktopMeaningCard]}>
                  <Card.Content>
                    <Title style={styles.meaningTitle}>
                      {getUniversalYearMeaning(result.universalYear).title}
                    </Title>
                    <Paragraph style={styles.meaningDescription}>
                      {getUniversalYearMeaning(result.universalYear).description}
                    </Paragraph>
                    
                    <View style={styles.themesContainer}>
                      <Text style={styles.themesTitle}>Key Themes:</Text>
                      <View style={styles.chipContainer}>
                        {getUniversalYearMeaning(result.universalYear).themes.map((theme, index) => (
                          <Chip 
                            key={index} 
                            style={styles.themeChip}
                            textStyle={styles.themeChipText}
                          >
                            {theme}
                          </Chip>
                        ))}
                      </View>
                    </View>

                    <Surface style={styles.adviceCard}>
                      <View style={styles.adviceHeader}>
                        <Ionicons name="bulb-outline" size={24} color="#4a3975" />
                        <Text style={styles.adviceTitle}>How to Navigate This Year:</Text>
                      </View>
                      <Paragraph style={styles.adviceText}>
                        {result.universalYear === 1 && "Embrace new opportunities and take initiative. This is a year for starting fresh projects and leading with confidence."}
                        {result.universalYear === 2 && "Focus on collaboration and partnerships. Practice patience and diplomacy in all your interactions."}
                        {result.universalYear === 3 && "Express yourself creatively and communicate openly. This is a year for social connections and artistic pursuits."}
                        {result.universalYear === 4 && "Build solid foundations and work steadily towards your goals. Focus on practical matters and long-term security."}
                        {result.universalYear === 5 && "Embrace change and new experiences. This is a year for adventure, travel, and breaking free from routines."}
                        {result.universalYear === 6 && "Focus on family, community, and service to others. This is a year for nurturing relationships and taking responsibility."}
                        {result.universalYear === 7 && "Seek inner wisdom and spiritual growth. This is a year for introspection, study, and deeper understanding."}
                        {result.universalYear === 8 && "Focus on material success and achievement. This is a year for business ventures and financial growth."}
                        {result.universalYear === 9 && "Complete unfinished business and serve humanity. This is a year for endings, wisdom, and humanitarian efforts."}
                        {result.universalYear === 11 && "Trust your intuition and seek higher consciousness. This is a year for spiritual illumination and inspiration."}
                        {result.universalYear === 22 && "Think big and build something lasting. This is a year for visionary projects and leaving a legacy."}
                      </Paragraph>
                    </Surface>
                  </Card.Content>
                </Card>
              )}
            </View>
          )}
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    padding: spacing.lg,
  },
  mainContent: {
    alignItems: 'center',
    paddingBottom: spacing.xxl,
  },
  
  // Input Section
  inputCard: {
    width: '100%',
    maxWidth: getCardMaxWidth(600),
    alignSelf: 'center',
    marginBottom: spacing.xxl,
    padding: spacing.xxl,
  },
  
  // Header Section
  headerContainer: {
    alignItems: 'center',
    marginBottom: spacing.xxl,
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  headerTitle: {
    ...typography.heading1,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.sm,
  },
  headerSubtitle: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    maxWidth: 300,
    lineHeight: 22,
  },
  
  // Input Section
  inputFieldContainer: {
    gap: spacing.xl,
  },
  fieldWrapper: {
    gap: spacing.sm,
  },
  fieldLabel: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
    marginLeft: spacing.sm,
  },
  inputWrapper: {
    backgroundColor: colors.white,
    borderRadius: borderRadius.lg,
    ...shadows.input,
    overflow: 'hidden',
  },
  inputWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    position: 'relative',
    paddingVertical: spacing.xs,
  },
  inputIcon: {
    position: 'absolute',
    left: spacing.lg,
    zIndex: 1,
  },
  modernInput: {
    flex: 1,
    backgroundColor: 'transparent',
    paddingLeft: spacing.xxxl + spacing.sm,
    elevation: 0,
    shadowOpacity: 0,
  },
  
  // Button Section
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.lg,
    marginTop: spacing.xl,
  },
  gradientButton: {
    borderRadius: borderRadius.xl,
    ...shadows.button,
    flex: 1,
    maxWidth: 300,
  },
  calculateButton: {
    backgroundColor: 'transparent',
    elevation: 0,
    shadowOpacity: 0,
  },
  calculateButtonContent: {
    height: 56,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
  },
  calculateButtonText: {
    ...typography.button,
    color: colors.white,
  },
  buttonIcon: {
    marginRight: spacing.xs,
  },
  resetButton: {
    borderColor: colors.primary,
    borderRadius: borderRadius.xl,
    backgroundColor: 'transparent',
  },
  resetButtonText: {
    ...typography.bodySmall,
    color: colors.primary,
    fontWeight: '600',
  },
  
  // Results Section
  resultsContainer: {
    width: '100%',
    maxWidth: getCardMaxWidth(700),
    alignSelf: 'center',
    gap: spacing.xxl,
  },
  resultCard: {
    ...commonStyles.resultCard,
    alignItems: 'center',
  },
  resultHeader: {
    alignItems: 'center',
    marginBottom: spacing.xl,
  },
  yearTitle: {
    ...typography.heading1,
    fontSize: 42,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  universalYearBadge: {
    borderRadius: 35,
    paddingHorizontal: spacing.xxxl,
    paddingVertical: spacing.xl,
    alignItems: 'center',
    ...shadows.result,
    overflow: 'hidden',
  },
  universalYearNumber: {
    ...typography.heading1,
    fontSize: 52,
    color: colors.white,
    fontWeight: '800',
  },
  universalYearLabel: {
    ...typography.caption,
    color: colors.white,
    fontWeight: '600',
    marginTop: spacing.xs,
  },
  calculationCard: {
    backgroundColor: colors.primary + '10',
    borderRadius: borderRadius.lg,
    ...shadows.input,
  },
  calculationLabel: {
    ...typography.caption,
    color: colors.textSecondary,
    marginBottom: spacing.xs,
  },
  calculationText: {
    ...typography.heading3,
    color: colors.primary,
  },
  meaningCard: {
    ...commonStyles.resultCard,
  },
  meaningTitle: {
    ...typography.heading2,
    color: colors.primary,
    textAlign: 'center',
    marginBottom: spacing.lg,
  },
  meaningDescription: {
    ...typography.body,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: spacing.xxl,
  },
  themesContainer: {
    marginBottom: spacing.xxl,
  },
  themesTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginBottom: spacing.lg,
  },
  chipContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.md,
  },
  themeChip: {
    backgroundColor: colors.primary + '15',
    borderRadius: borderRadius.pill,
    ...shadows.input,
  },
  themeChipText: {
    color: colors.primary,
    fontWeight: '600',
  },
  adviceCard: {
    backgroundColor: colors.primary + '05',
    borderRadius: borderRadius.lg,
    padding: spacing.xl,
    borderLeftWidth: 4,
    borderLeftColor: colors.primary,
  },
  adviceHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
  },
  adviceTitle: {
    ...typography.heading3,
    color: colors.primary,
    marginLeft: spacing.md,
  },
  adviceText: {
    ...typography.body,
    color: colors.textSecondary,
    lineHeight: 24,
  },
});